import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router'
import PageMeta from '../../components/common/PageMeta'
import PageBreadcrumb from '../../components/common/PageBreadCrumb'
import Button from '../../components/ui/button/Button'
import { Modal } from '../../components/ui/modal'
import { useModal } from '../../hooks/useModal'
import { useToast } from '../../contexts/ToastContext'
import {
  W3WizardSession,
  UploadedDocument,
  JustificationEntry,
  TaskStatusResponse,
  PlanVerificationPayload,
  PlanOptions,
  getSession,
  triggerParse,
  pollParseStatus,
  triggerReconciliation,
  saveJustifications,
  generateW3,
  pollPlanImportStatus,
  submitPlanVerification,
  getW3Form,
  uploadWbdImage,
  abandonSession,
  downloadW3Pdf,
} from '../../lib/api/w3-wizard'
import OperationsTimeline, { TimelineDay } from '../../components/nm/OperationsTimeline'
import PlugReconciliation, {
  PlugComparisonRow,
  ReconciliationSummary,
} from '../../components/nm/PlugReconciliation'
// These components will be created by another agent
import TicketUploader from '../../components/w3/TicketUploader'
import PlanVerification from '../../components/w3/PlanVerification'
import JustificationPanel from '../../components/w3/JustificationPanel'
import { API_BASE_URL } from '../../lib/config'
import AsPluggedWBD, {
  AsPluggedWBDHandle,
  transformReconciliationToAsPluggedData,
} from '../../components/diagrams/AsPluggedWBD'
import { captureSvgAsPng } from '../../utils/svgToImage'
import WbdExcelActions from '../../components/w3/WbdExcelActions'
import JurisdictionBadge from '../../components/ui/JurisdictionBadge'

// ---- Step config ----

type W3Step = 1 | 2 | 3 | 4 | 5 | 6

function getSteps(formType?: 'w3' | 'sundry'): { number: W3Step; label: string }[] {
  const formLabel = formType === 'sundry' ? 'Sundry' : 'W-3'
  return [
    { number: 1, label: 'Upload Tickets' },
    { number: 2, label: 'Review Events' },
    { number: 3, label: 'Reconciliation' },
    { number: 4, label: 'Justifications' },
    { number: 5, label: 'As Plugged Diagram' },
    { number: 6, label: `Generate ${formLabel}` },
  ]
}

// ---- Polling hook ----

function usePollParseStatus(sessionId: string, active: boolean) {
  const [status, setStatus] = useState<TaskStatusResponse | null>(null)

  useEffect(() => {
    if (!active || !sessionId) return
    const interval = setInterval(async () => {
      try {
        const res = await pollParseStatus(sessionId)
        setStatus(res)
        if (res.status === 'SUCCESS' || res.status === 'FAILURE') {
          clearInterval(interval)
        }
      } catch {
        clearInterval(interval)
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [sessionId, active])

  return status
}

// ---- Step Indicator ----

function StepIndicator({ currentStep, formType }: { currentStep: W3Step; formType?: 'w3' | 'sundry' }) {
  const steps = getSteps(formType)
  return (
    <nav className="flex items-center gap-0 mb-6">
      {steps.map((step, idx) => {
        const isDone = step.number < currentStep
        const isActive = step.number === currentStep

        return (
          <div key={step.number} className="flex items-center flex-1 last:flex-none">
            <div className="flex items-center gap-2 min-w-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-colors ${
                  isDone
                    ? 'bg-green-500 text-white'
                    : isActive
                    ? 'bg-brand-500 text-white'
                    : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                }`}
              >
                {isDone ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  step.number
                )}
              </div>
              <span
                className={`hidden sm:block text-sm font-medium whitespace-nowrap ${
                  isActive
                    ? 'text-brand-600 dark:text-brand-400'
                    : isDone
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-400 dark:text-gray-500'
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-2 transition-colors ${
                  step.number < currentStep
                    ? 'bg-green-400 dark:bg-green-600'
                    : 'bg-gray-200 dark:bg-gray-700'
                }`}
              />
            )}
          </div>
        )
      })}
    </nav>
  )
}

// ---- Session summary card ----

function SessionSummaryCard({ session }: { session: W3WizardSession }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base font-semibold text-gray-800 dark:text-white/90">
            Post Plugging Report — API {session.api_number}
          </h3>
          <JurisdictionBadge jurisdiction={session.jurisdiction} formType={session.form_type} />
        </div>
      </div>
      <div className="p-6 grid grid-cols-2 gap-4 sm:grid-cols-4 text-sm">
        {[
          { label: 'API Number', value: session.api_number },
          { label: 'Status', value: (session.status ?? 'created').replace(/_/g, ' ') },
          { label: 'Documents', value: String((session.uploaded_documents ?? []).length) },
          {
            label: 'Created',
            value: new Date(session.created_at).toLocaleDateString(),
          },
        ].map((item) => (
          <div key={item.label}>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              {item.label}
            </p>
            <p className="mt-0.5 font-semibold text-gray-800 dark:text-white/90 capitalize">
              {item.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

// ---- Helpers to map parse/reconciliation results ----

function mapParseResultToDays(parseResult: Record<string, unknown>): TimelineDay[] {
  const days = parseResult.days
  if (!Array.isArray(days)) return []
  return days.map((d: Record<string, unknown>, idx: number) => ({
    day_number: typeof d.day_number === 'number' ? d.day_number : idx + 1,
    work_date: typeof d.work_date === 'string' ? d.work_date : '',
    narrative: typeof d.narrative === 'string' ? d.narrative : '',
    events: Array.isArray(d.events)
      ? (d.events as Record<string, unknown>[]).map((ev) => ({
          event_type: typeof ev.event_type === 'string' ? ev.event_type : 'unknown',
          description: typeof ev.description === 'string' ? ev.description : '',
          depth_top_ft: typeof ev.depth_top_ft === 'number' ? ev.depth_top_ft : undefined,
          depth_bottom_ft:
            typeof ev.depth_bottom_ft === 'number' ? ev.depth_bottom_ft : undefined,
          sacks: typeof ev.sacks === 'number' ? ev.sacks : undefined,
        }))
      : [],
  }))
}

function mapReconciliationResult(reconciliationResult: Record<string, unknown>): {
  comparisons: PlugComparisonRow[]
  summary: ReconciliationSummary
  milestone_comparisons: Record<string, unknown>[]
} {
  const comparisons: PlugComparisonRow[] = Array.isArray(reconciliationResult.comparisons)
    ? (reconciliationResult.comparisons as Record<string, unknown>[]).map((r) => ({
        plug_number: typeof r.plug_number === 'number' ? r.plug_number : 0,
        planned_type: typeof r.planned_type === 'string' ? r.planned_type : undefined,
        planned_top_ft: typeof r.planned_top_ft === 'number' ? r.planned_top_ft : undefined,
        planned_bottom_ft:
          typeof r.planned_bottom_ft === 'number' ? r.planned_bottom_ft : undefined,
        planned_sacks: typeof r.planned_sacks === 'number' ? r.planned_sacks : undefined,
        actual_type: typeof r.actual_type === 'string' ? r.actual_type : undefined,
        actual_top_ft: typeof r.actual_top_ft === 'number' ? r.actual_top_ft : undefined,
        actual_bottom_ft:
          typeof r.actual_bottom_ft === 'number' ? r.actual_bottom_ft : undefined,
        actual_sacks: typeof r.actual_sacks === 'number' ? r.actual_sacks : undefined,
        actual_cement_class: typeof r.actual_cement_class === 'string' ? r.actual_cement_class : undefined,
        planned_cement_class: typeof r.planned_cement_class === 'string' ? r.planned_cement_class : undefined,
        actual_tagged_depth_ft: typeof r.actual_tagged_depth_ft === 'number' ? r.actual_tagged_depth_ft : undefined,
        // WP5: placement method, WOC, tagged
        planned_placement_method: typeof r.planned_placement_method === 'string' ? r.planned_placement_method : undefined,
        actual_placement_method: typeof r.actual_placement_method === 'string' ? r.actual_placement_method : undefined,
        planned_woc_hours: typeof r.planned_woc_hours === 'number' ? r.planned_woc_hours : undefined,
        actual_woc_hours: typeof r.actual_woc_hours === 'number' ? r.actual_woc_hours : undefined,
        actual_woc_tagged: typeof r.actual_woc_tagged === 'boolean' ? r.actual_woc_tagged : undefined,
        deviation_level:
          typeof r.deviation_level === 'string'
            ? (r.deviation_level as PlugComparisonRow['deviation_level'])
            : 'match',
        deviation_notes: Array.isArray(r.deviation_notes)
          ? (r.deviation_notes as string[])
          : [],
      }))
    : []

  const s = (reconciliationResult.summary ?? {}) as Record<string, unknown>
  const summary: ReconciliationSummary = {
    total_planned: typeof s.total_planned === 'number' ? s.total_planned : 0,
    total_actual: typeof s.total_actual === 'number' ? s.total_actual : 0,
    matches: typeof s.matches === 'number' ? s.matches : 0,
    minor_deviations: typeof s.minor_deviations === 'number' ? s.minor_deviations : 0,
    major_deviations: typeof s.major_deviations === 'number' ? s.major_deviations : 0,
    added_plugs: typeof s.added_plugs === 'number' ? s.added_plugs : 0,
    missing_plugs: typeof s.missing_plugs === 'number' ? s.missing_plugs : 0,
    overall_status: typeof s.overall_status === 'string' ? s.overall_status : 'Unknown',
  }

  const milestone_comparisons: Record<string, unknown>[] = Array.isArray(
    reconciliationResult.milestone_comparisons
  )
    ? (reconciliationResult.milestone_comparisons as Record<string, unknown>[])
    : []

  return { comparisons, summary, milestone_comparisons }
}

// ---- Main component ----

export default function W3Wizard() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const { success, error: showError } = useToast()

  const [session, setSession] = useState<W3WizardSession | null>(null)
  const [loading, setLoading] = useState(true)
  const [pageError, setPageError] = useState<string | null>(null)

  const [currentStep, setCurrentStep] = useState<W3Step>(1)

  // Step 2 — timeline (editable local copy)
  const [timelineDays, setTimelineDays] = useState<TimelineDay[]>([])

  // Step 3 — reconciliation loading
  const [reconciliationLoading, setReconciliationLoading] = useState(false)

  // Step 4 — justifications
  const [justifications, setJustifications] = useState<Record<string, JustificationEntry>>({})
  const [savingJustifications, setSavingJustifications] = useState(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Step 5 — As Plugged Diagram
  const wbdRef = useRef<AsPluggedWBDHandle>(null)
  const [capturingWbd, setCapturingWbd] = useState(false)

  // Step 6 — generation polling
  const [generating, setGenerating] = useState(false)
  const [generateTaskId, setGenerateTaskId] = useState<string | null>(null)
  const [generationComplete, setGenerationComplete] = useState(false)
  const [generationWarnings, setGenerationWarnings] = useState<string[]>([])
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [downloadingPdf, setDownloadingPdf] = useState(false)

  // Parse polling
  const [parsing, setParsing] = useState(false)
  const parseStatus = usePollParseStatus(sessionId ?? '', parsing)

  // Plan import polling
  const [planImporting, setPlanImporting] = useState(false)

  // Plan options (sent to reconciliation)
  const [planOptions, setPlanOptions] = useState<PlanOptions>({
    combine_nearby_plugs: false,
    combine_threshold_ft: 200,
  })

  // Abandon session
  const abandonModal = useModal()
  const [abandoning, setAbandoning] = useState(false)

  // React to parse polling result
  useEffect(() => {
    if (!parseStatus) return
    if (parseStatus.status === 'SUCCESS') {
      setParsing(false)
      success('Parsing Complete', 'Ticket data extracted successfully.')
      // Reload session to get updated parse_result
      if (sessionId) {
        getSession(sessionId)
          .then((s) => {
            setSession(s)
            setTimelineDays(mapParseResultToDays(s.parse_result))
            setCurrentStep(2)
          })
          .catch(() => {
            showError('Reload Failed', 'Could not reload session after parsing.')
          })
      }
    } else if (parseStatus.status === 'FAILURE') {
      setParsing(false)
      showError('Parsing Failed', 'Ticket parsing encountered an error. Please try again.')
    }
  }, [parseStatus])

  // Plan import polling
  useEffect(() => {
    if (!planImporting || !session?.id) return

    const interval = setInterval(async () => {
      try {
        const status = await pollPlanImportStatus(session.id)
        if (status.status === 'SUCCESS') {
          setPlanImporting(false)
          const updated = await getSession(session.id)
          setSession(updated)
          success('Plan Import', 'Operator plan imported successfully.')
        } else if (status.status === 'FAILURE') {
          setPlanImporting(false)
          showError('Plan Import Failed', status.error || 'Failed to import plan.')
        }
      } catch {
        // Polling error — will retry on next interval
      }
    }, 3000)

    return () => clearInterval(interval)
  }, [planImporting, session?.id])

  // Poll W-3 generation
  useEffect(() => {
    if (!generating || !sessionId) return
    const interval = setInterval(async () => {
      try {
        const res = await pollParseStatus(sessionId) // reuse parse-status endpoint pattern
        if (res.status === 'SUCCESS') {
          clearInterval(interval)
          setGenerating(false)
          setGenerationComplete(true)
          // Reload session for w3_generation_result
          getSession(sessionId).then((s) => {
            setSession(s)
            const warnings = s.w3_generation_result?.warnings
            if (Array.isArray(warnings)) {
              setGenerationWarnings(warnings as string[])
            }
          })
          // Fetch pdf_url from w3-form endpoint
          getW3Form(sessionId).then((formData) => {
            if (formData?.pdf_url) {
              const url = formData.pdf_url as string
              // Prepend API base URL if it's a relative path
              setPdfUrl(url.startsWith('http') ? url : `${API_BASE_URL}${url}`)
            }
          }).catch(() => {}) // non-fatal
          success(`${formLabel} Generated`, `${formLabel} form generated successfully.`)
        } else if (res.status === 'FAILURE') {
          clearInterval(interval)
          setGenerating(false)
          showError(`${formLabel} Generation Failed`, `${formLabel} generation failed. Please try again.`)
        }
      } catch {
        clearInterval(interval)
        setGenerating(false)
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [generating, sessionId, generateTaskId])

  // Load session on mount
  useEffect(() => {
    const load = async () => {
      if (!sessionId) {
        setPageError('Session ID is required.')
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        const s = await getSession(sessionId)
        setSession(s)
        setJustifications(s.justifications ?? {})

        // Hydrate step from server
        // Backward compat: old sessions stored 5=generate, now 5=WBD, 6=generate
        let rawStep = s.current_step
        if (rawStep === 5 && (s.status === 'completed' || s.status === 'generating')) {
          rawStep = 6
        }
        const step = Math.max(1, Math.min(6, rawStep)) as W3Step
        setCurrentStep(step)

        // Hydrate timeline if already parsed
        if (s.parse_result && Object.keys(s.parse_result).length > 0) {
          setTimelineDays(mapParseResultToDays(s.parse_result))
        }

        // Hydrate completed state — show PDF link instead of Generate button
        if (s.status === 'completed' && s.w3_generation_result) {
          setGenerationComplete(true)
          const genPdfUrl = s.w3_generation_result?.pdf_url as string | undefined
          if (genPdfUrl) {
            setPdfUrl(genPdfUrl.startsWith('http') ? genPdfUrl : `${API_BASE_URL}${genPdfUrl}`)
          }
        }
      } catch (err) {
        setPageError(err instanceof Error ? err.message : 'Failed to load session.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [sessionId])

  // Reload the session from the server (used after import or other mutations)
  const fetchSession = useCallback(async () => {
    if (!sessionId) return
    try {
      const s = await getSession(sessionId)
      setSession(s)
    } catch {
      // Silent — non-critical reload failure
    }
  }, [sessionId])

  // ---- Abandon handler ----

  const handleAbandonSession = async () => {
    if (!sessionId) return
    setAbandoning(true)
    try {
      await abandonSession(sessionId)
      success('Session Abandoned', 'The report session has been abandoned.')
      abandonModal.closeModal()
      navigate('/w3-wizard')
    } catch (err) {
      showError('Abandon Failed', err instanceof Error ? err.message : 'Failed to abandon session.')
    } finally {
      setAbandoning(false)
    }
  }

  // ---- Step handlers ----

  const handleParseTickets = async () => {
    if (!sessionId) return
    try {
      await triggerParse(sessionId)
      setParsing(true)
    } catch (err) {
      showError('Parse Failed', err instanceof Error ? err.message : 'Failed to start parsing.')
    }
  }

  const handlePlanUploadComplete = (docs: UploadedDocument[]) => {
    setSession((prev) =>
      prev
        ? {
            ...prev,
            uploaded_documents: [
              ...prev.uploaded_documents.filter((d) => d.category !== 'plan'),
              ...docs,
            ],
          }
        : prev
    )
    setPlanImporting(true)
  }

  const handlePlanVerified = async (payload: PlanVerificationPayload) => {
    if (!session?.id) return
    const updated = await submitPlanVerification(session.id, payload)
    setSession(updated)
    success('Plan Verified', 'Plan data confirmed. You can now parse tickets.')
  }

  const handlePlanReupload = () => {
    // Reset plan status by scrolling to uploader — the TicketUploader drop zone is visible
    // The session status will update once a new plan is uploaded and imported
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleRunReconciliation = async () => {
    if (!sessionId) return
    setReconciliationLoading(true)
    setCurrentStep(3)
    try {
      await triggerReconciliation(sessionId, planOptions)
      // Poll until done by reloading session after a short wait
      let attempts = 0
      const poll = setInterval(async () => {
        attempts++
        try {
          const s = await getSession(sessionId)
          if (
            s.status === 'reconciled' ||
            (s.reconciliation_result && Object.keys(s.reconciliation_result).length > 0) ||
            attempts > 30
          ) {
            clearInterval(poll)
            setSession(s)
            setReconciliationLoading(false)
          }
        } catch {
          clearInterval(poll)
          setReconciliationLoading(false)
        }
      }, 2000)
    } catch (err) {
      setReconciliationLoading(false)
      showError(
        'Reconciliation Failed',
        err instanceof Error ? err.message : 'Failed to run reconciliation.'
      )
    }
  }

  const handleSaveJustifications = useCallback(
    async (entries: Record<string, JustificationEntry>) => {
      if (!sessionId) return
      setSavingJustifications(true)
      try {
        const response = await saveJustifications(sessionId, entries)
        // Backend returns { justifications: {...}, session_status: "..." }
        // Extract just the justifications dict to avoid polluting state
        const updated = (response as Record<string, unknown>).justifications as Record<string, JustificationEntry> | undefined
        if (updated && typeof updated === 'object') {
          setJustifications(updated)
        }
      } catch (err) {
        showError(
          'Save Failed',
          err instanceof Error ? err.message : 'Failed to save justifications.'
        )
      } finally {
        setSavingJustifications(false)
      }
    },
    [sessionId]
  )

  // Debounced auto-save on justification change
  const handleJustificationsChange = (entries: Record<string, JustificationEntry>) => {
    setJustifications(entries)
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      handleSaveJustifications(entries)
    }, 1200)
  }

  const handleSaveAndExit = async () => {
    await handleSaveJustifications(justifications)
    navigate('/w3-wizard')
  }

  const handleDownloadPdf = async () => {
    if (!sessionId) return
    setDownloadingPdf(true)
    try {
      const blob = await downloadW3Pdf(sessionId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `plugging_report_${sessionId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      showError('Download Failed', err instanceof Error ? err.message : 'Failed to download PDF')
    } finally {
      setDownloadingPdf(false)
    }
  }

  const handleGenerateW3 = async () => {
    if (!sessionId) return
    try {
      const res = await generateW3(sessionId)
      setGenerateTaskId(res.task_id)
      setGenerating(true)
    } catch (err) {
      showError('Generation Failed', err instanceof Error ? err.message : 'Failed to start generation.')
    }
  }

  const handleCaptureAndContinue = async () => {
    // Capture SVG as PNG and upload, then move to step 6
    setCapturingWbd(true)
    try {
      const svgEl = wbdRef.current?.getSvgElement()
      if (svgEl && sessionId) {
        const blob = await captureSvgAsPng(svgEl)
        await uploadWbdImage(sessionId, blob)
      }
    } catch (err) {
      // Non-fatal: diagram just won't be in the PDF
      console.warn('WBD capture failed (non-fatal):', err)
    } finally {
      setCapturingWbd(false)
      setCurrentStep(6)
    }
  }

  const allJustificationsResolved = Object.values(justifications).every((j) => j.resolved)

  // ---- Derived reconciliation data ----
  const reconciliationData =
    session && session.reconciliation_result && Object.keys(session.reconciliation_result).length > 0
      ? mapReconciliationResult(session.reconciliation_result)
      : null

  // ---- Derived labels ----
  const formLabel = session?.form_type === 'sundry' ? 'Sundry' : 'W-3'

  // ---- Render guards ----

  if (loading) {
    return (
      <>
        <PageBreadcrumb pageTitle="Post Plugging Report" />
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
            <p className="mt-3 text-gray-600 dark:text-gray-400">Loading session...</p>
          </div>
        </div>
      </>
    )
  }

  if (pageError || !session) {
    return (
      <>
        <PageBreadcrumb pageTitle="Post Plugging Report" />
        <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-6">
          <div className="rounded-lg bg-error-50 p-4 dark:bg-error-500/15">
            <p className="text-sm font-medium text-error-700 dark:text-error-400">
              {pageError || 'Session not found.'}
            </p>
            <Button onClick={() => navigate('/w3-wizard')} className="mt-4" variant="outline">
              Back to Post Plugging Reports
            </Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageMeta
        title={`Post Plugging Report — ${session.api_number} | RegulAgent`}
        description="Generate post plugging report from ticket data"
      />

      <PageBreadcrumb pageTitle={`Post Plugging Report — ${session.api_number}`} />

      {session.status !== 'abandoned' && session.status !== 'completed' && (
        <div className="flex justify-end mb-4 -mt-2">
          <button
            onClick={abandonModal.openModal}
            className="text-sm text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors"
          >
            Abandon Session
          </button>
        </div>
      )}

      <StepIndicator currentStep={currentStep} formType={session.form_type} />

      {/* Session summary always visible */}
      <div className="mb-6">
        <SessionSummaryCard session={session} />
      </div>

      {/* No plan warning */}
      {!session.plan_snapshot && (
        <div className="mb-6 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 px-4 py-3">
          <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
            No approved plan found for this well — reconciliation will be skipped.
          </p>
        </div>
      )}

      {/* ---- Step 1: Upload Tickets ---- */}
      {currentStep === 1 && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Step 1: Upload Tickets
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Upload plug job ticket PDFs to extract operations data.
              </p>
            </div>
            <div className="p-6">
              <TicketUploader
                sessionId={session.id}
                existingDocuments={session.uploaded_documents ?? []}
                onUploadComplete={(docs) => {
                  setSession((prev) =>
                    prev
                      ? {
                          ...prev,
                          uploaded_documents: [
                            ...prev.uploaded_documents.filter((d) => d.category === 'plan'),
                            ...docs,
                          ],
                        }
                      : prev
                  )
                }}
                onDocumentsUpdated={(docs) => {
                  setSession((prev) =>
                    prev ? { ...prev, uploaded_documents: docs } : prev
                  )
                }}
                onError={(msg) => showError('Upload Failed', msg)}
                hasPlanSnapshot={!!session.plan_snapshot}
                planImporting={planImporting}
                onPlanUploadComplete={handlePlanUploadComplete}
                sessionStatus={session.status}
              />
            </div>
          </div>

          {session.status === 'plan_imported' && session.plan_snapshot_payload && (
            <PlanVerification
              payload={session.plan_snapshot_payload}
              onConfirm={handlePlanVerified}
              onReupload={handlePlanReupload}
            />
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleParseTickets}
              disabled={!session.uploaded_documents?.length || parsing || planImporting || session.status === 'plan_imported'}
            >
              {parsing ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
                  Parsing tickets...
                </span>
              ) : (
                'Parse Tickets'
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ---- Step 2: Review Events ---- */}
      {currentStep === 2 && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                    Step 2: Review Events
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Review the day-by-day operations timeline extracted from tickets.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {typeof session.parse_result?.confidence === 'number' && (
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        (session.parse_result?.confidence ?? 0) >= 0.8
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : (session.parse_result?.confidence ?? 0) >= 0.6
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }`}
                    >
                      Confidence: {Math.round(((session.parse_result?.confidence ?? 0) as number) * 100)}%
                    </span>
                  )}
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {timelineDays.length} day{timelineDays.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            {/* Compliance flags summary */}
            {(session.event_compliance_flags?.flags?.length ?? 0) > 0 && (
              <div className={`mx-6 mt-4 rounded-xl border px-4 py-3 ${
                (session.event_compliance_flags?.summary.violations ?? 0) > 0
                  ? 'border-red-200 bg-red-50 dark:border-red-800/30 dark:bg-red-900/10'
                  : (session.event_compliance_flags?.summary.warnings ?? 0) > 0
                  ? 'border-amber-200 bg-amber-50 dark:border-amber-800/30 dark:bg-amber-900/10'
                  : 'border-blue-200 bg-blue-50 dark:border-blue-800/30 dark:bg-blue-900/10'
              }`}>
                <div className="flex items-start gap-3">
                  <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                    (session.event_compliance_flags?.summary.violations ?? 0) > 0
                      ? 'text-red-500' : 'text-amber-500'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                      Regulatory Compliance Check
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      {[
                        (session.event_compliance_flags?.summary.violations ?? 0) > 0 && `${session.event_compliance_flags!.summary.violations} violation${session.event_compliance_flags!.summary.violations !== 1 ? 's' : ''}`,
                        (session.event_compliance_flags?.summary.warnings ?? 0) > 0 && `${session.event_compliance_flags!.summary.warnings} warning${session.event_compliance_flags!.summary.warnings !== 1 ? 's' : ''}`,
                        (session.event_compliance_flags?.summary.info ?? 0) > 0 && `${session.event_compliance_flags!.summary.info} info`,
                      ].filter(Boolean).join(', ')}
                      {' '}found — review flagged events before proceeding.
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      Policy: {session.event_compliance_flags?.jurisdiction} — {session.event_compliance_flags?.policy_id}
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="p-6">
              {timelineDays.length > 0 ? (
                <OperationsTimeline
                  days={timelineDays}
                  editable
                  onDaysChange={setTimelineDays}
                  complianceFlags={session?.event_compliance_flags?.flags}
                />
              ) : (
                <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                  No events extracted yet.
                </div>
              )}
            </div>
          </div>

          {/* Plan Options */}
          <div className="mb-4 rounded-xl border border-gray-200 dark:border-white/[0.05] bg-white dark:bg-white/[0.03] p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Plan Options
              </h4>
            </div>

            {/* Combine nearby plugs toggle */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="combine-plugs"
                checked={planOptions.combine_nearby_plugs}
                onChange={(e) =>
                  setPlanOptions(prev => ({
                    ...prev,
                    combine_nearby_plugs: e.target.checked,
                  }))
                }
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
              />
              <div className="flex-1">
                <label
                  htmlFor="combine-plugs"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                  Combine nearby plugs
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Surface plug will absorb shoe and groundwater plugs within the specified distance.
                </p>

                {/* Threshold input — only shown when toggle is on */}
                {planOptions.combine_nearby_plugs && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-gray-600 dark:text-gray-400">
                      Combine plugs within
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={2000}
                      step={50}
                      value={planOptions.combine_threshold_ft}
                      onChange={(e) =>
                        setPlanOptions(prev => ({
                          ...prev,
                          combine_threshold_ft: Number(e.target.value),
                        }))
                      }
                      className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-white/[0.1] rounded-lg bg-white dark:bg-white/[0.05] text-gray-800 dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">ft of each other</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep(1)}>
              Re-upload
            </Button>
            <Button onClick={handleRunReconciliation}>
              Run Reconciliation
            </Button>
          </div>
        </div>
      )}

      {/* ---- Step 3: Reconciliation ---- */}
      {currentStep === 3 && (
        <div className="space-y-6">
          {reconciliationLoading ? (
            <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-12">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent" />
                <p className="mt-3 text-gray-600 dark:text-gray-400">
                  Running reconciliation...
                </p>
              </div>
            </div>
          ) : reconciliationData ? (
            <>
              <PlugReconciliation
                comparisons={reconciliationData.comparisons}
                summary={reconciliationData.summary}
              />
              {/* Auto-approved variances notice */}
              {reconciliationData.comparisons.some(
                (c) => c.deviation_level === 'minor'
              ) && (
                <div className="rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 px-4 py-3">
                  <p className="text-sm text-amber-800 dark:text-amber-300 font-medium">
                    Minor variances detected — review justifications in Step 4.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No reconciliation data available.
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep(2)}>
              Back
            </Button>
            <Button
              onClick={() => setCurrentStep(4)}
              disabled={!reconciliationData}
            >
              Continue to Justifications
            </Button>
          </div>
        </div>
      )}

      {/* ---- Step 4: Justifications ---- */}
      {currentStep === 4 && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                    Step 4: Justifications
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Provide justifications for major, missing, and added plug divergences.
                  </p>
                </div>
                {savingJustifications && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    Saving...
                  </span>
                )}
              </div>
            </div>
            <div className="p-6">
              <JustificationPanel
                divergences={
                  (reconciliationData?.comparisons.filter((c) =>
                    ['major', 'missing', 'added'].includes(c.deviation_level)
                  ) ?? []) as any[]
                }
                justifications={justifications}
                onChange={handleJustificationsChange}
                onBlur={() => handleSaveJustifications(justifications)}
              />
            </div>
          </div>

          <div className="flex justify-between flex-wrap gap-3">
            <Button variant="outline" onClick={() => setCurrentStep(3)}>
              Back
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleSaveAndExit}>
                Save &amp; Exit
              </Button>
              <Button
                onClick={() => setCurrentStep(5)}
                disabled={!allJustificationsResolved}
              >
                Continue to Diagram
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Step 5: As Plugged Diagram ---- */}
      {currentStep === 5 && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Step 5: As Plugged Wellbore Diagram
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                Review the actual end state of the well after plugging operations.
              </p>
            </div>
            <div className="p-6">
              {(() => {
                const diagramData = reconciliationData
                  ? transformReconciliationToAsPluggedData(
                      reconciliationData.comparisons,
                      session?.plan_snapshot_well_geometry ?? null,
                      {
                        api14: session?.api_number ?? '',
                        operator_name: session?.plan_snapshot_well_header?.operator ?? session?.plan_snapshot_well_header?.operator_name ?? '',
                        field_name: session?.plan_snapshot_well_header?.field_name ?? '',
                        well_number: session?.plan_snapshot_well_header?.well_name ?? session?.plan_snapshot_well_header?.well_number ?? '',
                        lease_name: session?.plan_snapshot_well_header?.lease_name ?? '',
                      },
                      (session?.jurisdiction as 'TX' | 'NM') ?? 'TX',
                      reconciliationData.milestone_comparisons,  // NEW
                    )
                  : null

                if (!diagramData) {
                  return (
                    <div className="text-center py-12 text-gray-500">
                      <p className="text-sm">
                        No reconciliation data available to generate the as-plugged diagram.
                      </p>
                      <p className="text-xs mt-1 text-gray-400">
                        You can proceed to generate without a diagram.
                      </p>
                    </div>
                  )
                }

                return <AsPluggedWBD ref={wbdRef} data={diagramData} />
              })()}
            </div>
          </div>

          {/* Wellbore Data Review */}
          {session?.plan_snapshot_well_geometry && (
            <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
              <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                  Wellbore Data Review
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  Verify the wellbore data used to generate the diagram above.
                </p>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Formation Tops */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-white/80 mb-2">
                    Formation Tops
                  </h4>
                  {(() => {
                    const formations = session.plan_snapshot_well_geometry?.formation_tops ?? []
                    if (!formations.length) return <p className="text-xs text-gray-400">No formation data available</p>
                    return (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-100 dark:border-white/[0.05]">
                            <th className="text-left py-1 font-medium text-gray-500">Formation</th>
                            <th className="text-right py-1 font-medium text-gray-500">Top (ft)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formations.map((f: any, i: number) => (
                            <tr key={i} className="border-b border-gray-50 dark:border-white/[0.02]">
                              <td className="py-1 text-gray-700 dark:text-white/70">{f.formation || f.formation_name || 'Unknown'}</td>
                              <td className="py-1 text-right text-gray-700 dark:text-white/70">{f.top_ft != null ? `${Number(f.top_ft).toLocaleString()}'` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  })()}
                </div>

                {/* Casing Record */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-white/80 mb-2">
                    Casing Record
                  </h4>
                  {(() => {
                    const casings = session.plan_snapshot_well_geometry?.casing_strings ?? []
                    if (!casings.length) return <p className="text-xs text-gray-400">No casing data available</p>
                    return (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-gray-100 dark:border-white/[0.05]">
                            <th className="text-left py-1 font-medium text-gray-500">Size</th>
                            <th className="text-left py-1 font-medium text-gray-500">Type</th>
                            <th className="text-right py-1 font-medium text-gray-500">Shoe (ft)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {casings.map((c: any, i: number) => (
                            <tr key={i} className="border-b border-gray-50 dark:border-white/[0.02]">
                              <td className="py-1 text-gray-700 dark:text-white/70">{c.od_inches ?? c.size_inches ?? c.size ?? '?'}"</td>
                              <td className="py-1 text-gray-700 dark:text-white/70">{c.casing_type ?? c.type ?? ''}</td>
                              <td className="py-1 text-right text-gray-700 dark:text-white/70">{c.shoe_depth_ft ?? c.bottom_ft ?? '?'}'</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  })()}
                </div>

                {/* Existing Tools */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-white/80 mb-2">
                    Tools in Hole
                  </h4>
                  {(() => {
                    const tools = session.plan_snapshot_well_geometry?.existing_tools ?? []
                    if (!tools.length) return <p className="text-xs text-green-600 font-medium">No tools in hole</p>
                    return (
                      <ul className="space-y-1">
                        {tools.map((t: any, i: number) => (
                          <li key={i} className="text-xs text-gray-700 dark:text-white/70">
                            {t.type ?? t.tool_type ?? 'Unknown'}{t.depth_ft ?? t.top_ft ? ` @ ${t.depth_ft ?? t.top_ft}'` : ''}{t.description ? ` — ${t.description}` : ''}
                          </li>
                        ))}
                      </ul>
                    )
                  })()}
                </div>

                {/* Perforations */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-white/80 mb-2">
                    Perforations
                  </h4>
                  {(() => {
                    const perfs = session.plan_snapshot_well_geometry?.perforations ?? session.plan_snapshot_well_geometry?.production_perforations ?? []
                    if (!perfs.length) return <p className="text-xs text-gray-400">No perforations</p>
                    return (
                      <ul className="space-y-1">
                        {perfs.map((p: any, i: number) => (
                          <li key={i} className="text-xs text-gray-700 dark:text-white/70">
                            {p.top_ft ?? p.depth_top_ft ?? '?'}' — {p.bottom_ft ?? p.depth_bottom_ft ?? '?'}'{p.formation ? ` (${p.formation})` : ''}
                          </li>
                        ))}
                      </ul>
                    )
                  })()}
                </div>
              </div>
            </div>
          )}

          {sessionId && session && (
            <WbdExcelActions
              sessionId={sessionId}
              apiNumber={session.api_number}
              onImportSuccess={() => {
                // Re-fetch session to get updated reconciliation data
                fetchSession()
              }}
            />
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep(4)}>
              Back
            </Button>
            <Button onClick={handleCaptureAndContinue} disabled={capturingWbd}>
              {capturingWbd ? (
                <span className="flex items-center gap-2">
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
                  Capturing...
                </span>
              ) : (
                `Continue to Generate`
              )}
            </Button>
          </div>
        </div>
      )}

      {/* ---- Step 6: Generate ---- */}
      {currentStep === 6 && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="border-b border-gray-200 px-6 py-4 dark:border-white/[0.05]">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">
                Step 6: {generationComplete ? `${formLabel} Complete` : `Generate ${formLabel}`}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {generationComplete
                  ? `${formLabel} form has been generated successfully.`
                  : `All divergences resolved. Ready to generate ${formLabel} form.`}
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Resolved divergences summary */}
              {Object.keys(justifications).length > 0 && (
                <div className="rounded-xl bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.05] p-4">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
                    Resolved Divergences
                  </p>
                  <div className="space-y-2">
                    {Object.entries(justifications).map(([key, entry]) => (
                      <div key={key} className="flex items-start gap-2 text-sm">
                        <span
                          className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                            entry.resolved ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                        />
                        <div className="min-w-0">
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Plug {key}:
                          </span>{' '}
                          <span className="text-gray-600 dark:text-gray-400">{entry.note}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Generation result */}
              {generationComplete && (
                <div className="rounded-xl border border-green-200 dark:border-green-800/40 bg-green-50 dark:bg-green-900/10 p-4">
                  <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                    {formLabel} form generated successfully.
                  </p>
                  <div className="mt-3 flex items-center gap-3">
                    {pdfUrl && (
                      <button
                        onClick={handleDownloadPdf}
                        disabled={downloadingPdf}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {downloadingPdf ? (
                          <>
                            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
                            Downloading…
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            Download {formLabel} PDF
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Validation warnings */}
              {generationWarnings.length > 0 && (
                <div className="rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-900/10 p-4">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase mb-2">
                    Validation Warnings
                  </p>
                  <ul className="space-y-1">
                    {generationWarnings.map((w, i) => (
                      <li key={i} className="text-sm text-amber-800 dark:text-amber-300">
                        - {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep(5)} disabled={generating}>
              Back
            </Button>
            {!generationComplete && (
              <Button onClick={handleGenerateW3} disabled={generating}>
                {generating ? (
                  <span className="flex items-center gap-2">
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
                    Generating {formLabel}...
                  </span>
                ) : (
                  `Generate ${formLabel}`
                )}
              </Button>
            )}
            {generationComplete && (
              <Button onClick={() => navigate('/w3-wizard')}>
                Done
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Abandon Confirmation Modal */}
      <Modal
        isOpen={abandonModal.isOpen}
        onClose={abandonModal.closeModal}
        className="max-w-[500px] p-5 lg:p-10"
      >
        <div className="text-center">
          <div className="relative flex items-center justify-center z-1 mb-7">
            <svg
              className="fill-error-50 dark:fill-error-500/15"
              width="90"
              height="90"
              viewBox="0 0 90 90"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M34.364 6.85053C38.6205 -2.28351 51.3795 -2.28351 55.636 6.85053C58.0129 11.951 63.5594 14.6722 68.9556 13.3853C78.6192 11.0807 86.5743 21.2433 82.2185 30.3287C79.7862 35.402 81.1561 41.5165 85.5082 45.0122C93.3019 51.2725 90.4628 63.9451 80.7747 66.1403C75.3648 67.3661 71.5265 72.2695 71.5572 77.9156C71.6123 88.0265 60.1169 93.6664 52.3918 87.3184C48.0781 83.7737 41.9219 83.7737 37.6082 87.3184C29.8831 93.6664 18.3877 88.0266 18.4428 77.9156C18.4735 72.2695 14.6352 67.3661 9.22531 66.1403C-0.462787 63.9451 -3.30193 51.2725 4.49185 45.0122C8.84391 41.5165 10.2138 35.402 7.78151 30.3287C3.42572 21.2433 11.3808 11.0807 21.0444 13.3853C26.4406 14.6722 31.9871 11.951 34.364 6.85053Z"
                fill=""
                fillOpacity=""
              />
            </svg>
            <span className="absolute -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2">
              <svg
                className="fill-error-600 dark:fill-error-500"
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.62684 11.7496C9.04105 11.1638 9.04105 10.2141 9.62684 9.6283C10.2126 9.04252 11.1624 9.04252 11.7482 9.6283L18.9985 16.8786L26.2485 9.62851C26.8343 9.04273 27.7841 9.04273 28.3699 9.62851C28.9556 10.2143 28.9556 11.164 28.3699 11.7498L21.1198 18.9999L28.3699 26.25C28.9556 26.8358 28.9556 27.7855 28.3699 28.3713C27.7841 28.9571 26.8343 28.9571 26.2485 28.3713L18.9985 21.1212L11.7482 28.3715C11.1624 28.9573 10.2126 28.9573 9.62684 28.3715C9.04105 27.7857 9.04105 26.836 9.62684 26.2502L16.8771 18.9999L9.62684 11.7496Z"
                  fill=""
                />
              </svg>
            </span>
          </div>

          <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90 sm:text-title-sm">
            Abandon Report?
          </h4>
          <p className="text-sm leading-6 text-gray-500 dark:text-gray-400">
            Are you sure you want to abandon this session for well{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {session.api_number}
            </span>
            ? This action cannot be undone.
          </p>

          <div className="flex items-center justify-center w-full gap-3 mt-7">
            <Button
              size="sm"
              variant="outline"
              onClick={abandonModal.closeModal}
              disabled={abandoning}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleAbandonSession}
              disabled={abandoning}
              className="bg-error-500 hover:bg-error-600"
            >
              {abandoning ? 'Abandoning...' : 'Abandon Session'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
